// „Продадено" при разнасяне — СГЪВАЕМ панел САМО с продаденото от отчета:
// рецепта/продукт · к-во (без ед. цена и оборот). Работата е в „Изписване от
// склад", затова панелът е вторичен:
//  • широк детайл (контейнерът ≥ 1040px — `@container` в ReportPostingDetail):
//    колона вдясно от изписването, винаги разгъната;
//  • по-тясно (лаптоп, таблет): лента „Продадено · 24 позиции · 312 бр ▸" НАД
//    таблицата, СВИТА по подразбиране; изборът се помни в този браузър.
// Редовете са с височината на справките (`POSTING_READ_ROW`, 34px), без колона
// „Тип" — продуктът (не рецепта) е малък маркер след името.
// Разбивката по сервитьори и станции НЕ е тук — тя е при отчета, под избраната
// карта в списъка с чакащите (`PendingReportBreakdown.tsx`). `headerSlot` е
// място в заглавието (вдясно) за бъдещо действие; днес не се подава.
import { useId, useState, type ReactNode } from "react";
import { RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ReportPostingRecipe } from "../../../../api/inventory";
import { CARD_HEAD, ROW_HEADER, ROW_HEADER_TEXT } from "../../components/list/density";
import { TruncatedText } from "../../components/list/TruncatedText";
import { formatCount } from "./reportPostingShared";
import { POSTING_READ_ROW } from "./postingDensity";

// Свито/разгънато на тесния изглед — в localStorage; без хранилище (частен
// прозорец, забранено) панелът работи, само не помни
const SOLD_OPEN_STORAGE_KEY = "inventory_posting_sold_open";

const readSoldOpen = (): boolean => {
  try {
    return localStorage.getItem(SOLD_OPEN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const writeSoldOpen = (open: boolean) => {
  try {
    localStorage.setItem(SOLD_OPEN_STORAGE_KEY, open ? "1" : "0");
  } catch {
    // пълно/забранено хранилище — изборът важи до презареждане
  }
};

// Една решетка за заглавния ред и всички редове — колоните се застъпват точно
const GRID_CLASS = "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 px-3.5";

type Props = {
  soldItems: ReportPostingRecipe[];
  reportNumber?: number | null;
  // място вдясно в заглавието; по избор
  headerSlot?: ReactNode;
};

export const ReportPostingSoldCard = ({ soldItems, reportNumber = null, headerSlot }: Props) => {
  const { t } = useTranslation();
  const bodyId = useId();
  const [open, setOpen] = useState(readSoldOpen);
  const toggle = () => {
    writeSoldOpen(!open);
    setOpen(!open);
  };

  const totalQty = soldItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const summary = [
    reportNumber != null ? t("inventoryPage.postingReportShort", { number: reportNumber }) : null,
    t("inventoryPage.postingSoldPositions", { count: soldItems.length }),
    soldItems.length ? t("inventoryPage.postingSoldTotalQty", { qty: formatCount(totalQty) }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const title = (
    <>
      <span className="shrink-0 text-[15px] font-semibold leading-[normal] text-ink">
        {t("inventoryPage.postingSoldTitle")}
      </span>
      <span className="min-w-0 truncate text-[12.5px] leading-[normal] text-muted">{summary}</span>
    </>
  );

  return (
    <section
      data-sold-panel
      className="min-w-0 overflow-hidden rounded-xl border border-shade-2 bg-panel"
    >
      <div className={`flex items-center gap-2 ${CARD_HEAD}`}>
        {/* Тесен изглед: лентата е бутон, който свива/разгъва */}
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={bodyId}
          title={open ? t("inventoryPage.postingSoldHide") : t("inventoryPage.postingSoldShow")}
          className="flex min-h-7 min-w-0 flex-1 cursor-pointer items-baseline gap-2 rounded-md border-none bg-transparent p-0 text-left outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] pointer-coarse:min-h-10 @min-[1040px]:hidden"
        >
          {title}
          <RightOutlined
            aria-hidden
            className={`ml-auto shrink-0 self-center text-[10px] text-muted transition-transform duration-150 ${
              open ? "rotate-90" : ""
            }`}
          />
        </button>
        {/* Широк изглед: колоната е винаги разгъната — само заглавие */}
        <div className="hidden min-w-0 flex-1 items-baseline gap-2 @min-[1040px]:flex">{title}</div>
        {headerSlot}
      </div>

      <div id={bodyId} className={`${open ? "" : "hidden"} @min-[1040px]:block`}>
        <div className={`${GRID_CLASS} ${ROW_HEADER} border-y border-shade-2 bg-fill`}>
          <div className={ROW_HEADER_TEXT}>{t("inventoryPage.postingColItem")}</div>
          <div className={`text-right ${ROW_HEADER_TEXT}`}>{t("inventoryPage.colQtyShort")}</div>
        </div>
        {soldItems.map((item) => (
          <div
            key={item.recipeId}
            className={`${GRID_CLASS} ${POSTING_READ_ROW} border-b border-shade-1 last:border-b-0`}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <TruncatedText text={item.name} className="text-ink" />
              {item.isProduct && (
                <span className="shrink-0 rounded bg-fill-2 px-1.5 text-[10.5px] font-medium leading-4 text-body">
                  {t("inventoryPage.postingProductTag")}
                </span>
              )}
            </span>
            <span className="text-right font-sheet text-ink tabular-nums">{formatCount(item.quantity)}</span>
          </div>
        ))}
        {soldItems.length === 0 && (
          <div className="px-3.5 py-2.5 text-[13px] leading-[normal] text-muted">
            {t("inventoryPage.noLines")}
          </div>
        )}
      </div>
    </section>
  );
};

export default ReportPostingSoldCard;
